import { useEffect, useState } from 'react';
import { getCategoryTree, type CategoryTreeNode } from '../api.js';
import { leafLabel } from './format.js';

/**
 * The category tree, fetched once per page load and shared by every Money tab.
 * It's static server data (taxonomy.ts), so one request covers the picker,
 * the ledger labels and the spending lists.
 */

export interface FlatCategory {
  path: string;
  label: string;
  /** Labels from the root down: ["Lifestyle", "Entertainment", "Video games"]. */
  chain: string[];
  node: CategoryTreeNode;
}

let cache: Promise<CategoryTreeNode[]> | null = null;

export function loadCategoryTree(): Promise<CategoryTreeNode[]> {
  if (!cache) {
    cache = getCategoryTree().catch((err: unknown) => {
      cache = null; // a failed load retries next time rather than sticking
      throw err;
    });
  }
  return cache;
}

/** Tests only: forget the cached tree. */
export function resetCategoryTree(): void {
  cache = null;
}

export function flattenTree(tree: CategoryTreeNode[]): FlatCategory[] {
  const out: FlatCategory[] = [];
  const walk = (nodes: CategoryTreeNode[], chain: string[]): void => {
    for (const node of nodes) {
      const c = [...chain, node.label];
      out.push({ path: node.path, label: node.label, chain: c, node });
      if (node.children) walk(node.children, c);
    }
  };
  walk(tree, []);
  return out;
}

export interface CategoryIndex {
  tree: CategoryTreeNode[];
  flat: FlatCategory[];
  byPath: Map<string, FlatCategory>;
  /** "Lifestyle › Entertainment › Video games"; falls back to the leaf for unknown paths. */
  full: (path: string | null | undefined) => string;
  /** "Video games". */
  leaf: (path: string | null | undefined) => string;
}

export function indexTree(tree: CategoryTreeNode[]): CategoryIndex {
  const flat = flattenTree(tree);
  const byPath = new Map(flat.map((f) => [f.path, f]));
  return {
    tree,
    flat,
    byPath,
    full: (p) => byPath.get(p ?? 'uncategorized')?.chain.join(' › ') ?? leafLabel(p),
    leaf: (p) => byPath.get(p ?? 'uncategorized')?.label ?? leafLabel(p),
  };
}

const EMPTY = indexTree([]);

/** The tree as an index; empty (leaf-label fallbacks) until it arrives. */
export function useCategoryTree(): CategoryIndex {
  const [index, setIndex] = useState<CategoryIndex>(EMPTY);
  useEffect(() => {
    let live = true;
    loadCategoryTree().then(
      (tree) => live && setIndex(indexTree(tree)),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);
  return index;
}
